import React from 'react';
import { fmtDate, fmtRelativeTime } from './lib/format';

const ICON = 'w-3.5 h-3.5 shrink-0';
const svgProps = {
  viewBox: '0 0 24 24', fill: 'none', stroke: 'currentColor', strokeWidth: 1.8,
  strokeLinecap: 'round', strokeLinejoin: 'round', 'aria-hidden': true,
};

/** Window (period) — a plain calendar. */
const CalendarIcon = () => (
  <svg className={ICON} {...svgProps}><rect x="3" y="4" width="18" height="18" rx="2" /><path d="M16 2v4M8 2v4M3 10h18" /></svg>
);
/** As-of date — a calendar with a check: the snapshot this data is true up to. */
const CalendarCheckIcon = () => (
  <svg className={ICON} {...svgProps}><rect x="3" y="4" width="18" height="18" rx="2" /><path d="M16 2v4M8 2v4M3 10h18M8.5 16l2 2 4-4" /></svg>
);
/** Stale — a clock: waiting on the nightly job. */
const ClockIcon = () => (
  <svg className={ICON} {...svgProps}><circle cx="12" cy="12" r="9" /><path d="M12 7.5V12l3 2" /></svg>
);
/** Freshness — a refresh arrow. */
const RefreshIcon = () => (
  <svg className={ICON} {...svgProps}><path d="M21 12a9 9 0 1 1-2.64-6.36" /><path d="M21 4v5h-5" /></svg>
);

/**
 * One meta item beneath the verdict. The window and as-of read as small tinted chips (the
 * context the number is true *for*); freshness reads as lighter plain text (the least
 * important of the three). No alert colours — a stale note is muted, never a warning (§12).
 */
const MetaChip = ({ icon, subtle = false, children }) => (
  <span
    className={`inline-flex items-center gap-1.5 rounded-md px-2 py-1 text-xs font-medium ${
      subtle
        ? 'text-gray-400 dark:text-gray-500'
        : 'bg-gray-100 dark:bg-gray-800/60 text-gray-600 dark:text-gray-300'
    }`}
  >
    {icon}
    {children}
  </span>
);

/**
 * The page verdict — the loudest text on the page (Part-B Tier 1). The owner reads
 * this and is done, so it leads in its own clean white card.
 *
 * The date context lives WITH the verdict rather than in the page header: the window
 * label (`period`), the as-of date, the freshness line and the Pacific-time note are
 * all what the verdict number is true *for*, so they read as one block beneath it.
 *
 * `verdict` may be null — the page hides the whole Tier 1 block in that case
 * (§4.5, third state), so this component assumes a verdict when rendered but still
 * guards against an empty string rather than printing a placeholder.
 *
 * `isStale` is a small muted note, never a warning: a stale flag means the nightly
 * job has not run yet, which is not the owner's problem to act on (§G.3).
 */
const VerdictHeader = ({ verdict, period, asOfDate, computedAt, isStale, timezoneNote }) => {
  if (!verdict) return null;

  return (
    <div className="bg-white dark:bg-gray-900 rounded-xl border border-gray-200 dark:border-gray-800 shadow-sm px-5 py-5 md:px-6">
      <p
        data-testid="verdict"
        className="gi-verdict font-medium leading-snug text-gray-900 dark:text-gray-100"
      >
        {verdict}
      </p>
      <div className="mt-3 flex flex-wrap items-center gap-2">
        {period && (
          <MetaChip icon={<CalendarIcon />}>{period.charAt(0).toUpperCase() + period.slice(1)}</MetaChip>
        )}
        {asOfDate && <MetaChip icon={<CalendarCheckIcon />}>As of {fmtDate(asOfDate)}</MetaChip>}
        {isStale && <MetaChip icon={<ClockIcon />}>Waiting on today&apos;s update</MetaChip>}
        {computedAt && (
          <MetaChip icon={<RefreshIcon />} subtle>Updated {fmtRelativeTime(computedAt)}</MetaChip>
        )}
      </div>
      {/* §9-T: the one _note an owner reads — why these dates will not match the
          Analytics page. Rendered verbatim; never converted. */}
      {timezoneNote && (
        <p className="mt-1 text-xs text-gray-400 dark:text-gray-500">{timezoneNote}</p>
      )}
    </div>
  );
};

export default VerdictHeader;
